import React, { useEffect, useState } from "react";
import { Handshake } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";

export default function About() {
  const { t, lang } = useLang();
  const [team, setTeam] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    api.get("/team")
      .then((r) => {
        setTeam(r.data.filter((m) => m.kind === "team"));
        setPartners(r.data.filter((m) => m.kind === "partner"));
      })
      .catch(() => {});
  }, []);

  const values = lang === "en"
    ? [
        { n: "01", title: "Artist first", text: "The artist is the priority. Everything we do serves the vision, sound and career of who we manage." },
        { n: "02", title: "Transparency", text: "Numbers, contracts and strategies are always on the table. No smoke, just concrete deliverables." },
        { n: "03", title: "Execution", text: "Great ideas without execution are useless. We work in short cycles with measurable outcomes." },
        { n: "04", title: "Long-term", text: "We don't chase viral moments. We build careers that last." },
      ]
    : [
        { n: "01", title: "Artista al centro", text: "L'artista è la priorità. Ogni decisione serve la visione, il sound e la carriera di chi gestiamo." },
        { n: "02", title: "Trasparenza", text: "Numeri, contratti, strategie sempre sul tavolo. Zero fumo, solo deliverable concreti." },
        { n: "03", title: "Esecuzione", text: "Le grandi idee senza esecuzione sono inutili. Lavoriamo per cicli brevi e risultati misurabili." },
        { n: "04", title: "Lungo periodo", text: "Non rincorriamo il momento virale. Costruiamo carriere che durano." },
      ];
  void values; // kept for fallback reference; values now come from CMS

  return (
    <div data-testid="about-page">
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 pt-16 pb-20">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F] mb-4">— Chi siamo</div>
        <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.9] text-[#0A0A0A] max-w-4xl">
          {t("about.title", "Chi siamo")}
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-[#0A0A0A]/70">{t("about.subtitle", "")}</p>
      </section>

      <section className="border-y-2 border-[#0A0A0A] bg-[#F2F2EC]">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 grid lg:grid-cols-2 gap-16">
          <div>
            <h2 className="font-display text-4xl uppercase mb-6 text-[#0A0A0A]">{t("about.mission.title", "Mission")}</h2>
            <p className="text-lg leading-relaxed text-[#0A0A0A]/80">{t("about.mission.text", "")}</p>
          </div>
          <div>
            <h2 className="font-display text-4xl uppercase mb-6 text-[#0A0A0A]">{t("about.story.title", "Storia")}</h2>
            <p className="text-lg leading-relaxed text-[#0A0A0A]/80">{t("about.story.text", "")}</p>
          </div>
        </div>
      </section>

      {/* Team + Partners — TEMPORANEAMENTE NASCOSTO (standby, riattivabile in futuro) */}
      {false && (
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24" data-testid="about-team-section">
        <div className="max-w-4xl">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F] mb-4">
            {t("about.team.eyebrow", "— Il team")}
          </div>
          <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] text-[#0A0A0A]">
            {t("about.team.title", "Il nostro team e le nostre collaborazioni")}
          </h2>
          <p className="mt-6 text-lg text-[#0A0A0A]/70">
            {t("about.team.subtitle", "")}
          </p>
        </div>

        {/* Team members */}
        {team.length > 0 && (
          <div className="mt-14" data-testid="about-team-grid">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-[#0A0A0A]/20" />
              <span className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60">
                {t("about.team.team_label", "Team")}
              </span>
              <div className="h-px flex-1 bg-[#0A0A0A]/20" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {team.map((m) => (
                <div
                  key={m.id}
                  className="artist-card border-2 border-[#0A0A0A] bg-[#FAFAF7] overflow-hidden"
                  data-testid={`team-card-${m.id}`}
                >
                  <div className="aspect-square overflow-hidden bg-[#F2F2EC]">
                    {m.image_url ? (
                      <img
                        src={m.image_url}
                        alt={m.name}
                        className="w-full h-full object-cover artist-image"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl font-display text-[#0A0A0A]/30">
                        {m.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">
                      {m[`role_${lang}`] || m.role_it}
                    </div>
                    <div className="mt-1 font-display text-xl uppercase text-[#0A0A0A]">{m.name}</div>
                    {(m[`bio_${lang}`] || m.bio_it) && (
                      <p className="mt-2 text-xs text-[#0A0A0A]/70 leading-relaxed line-clamp-3">
                        {m[`bio_${lang}`] || m.bio_it}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Partners */}
        {partners.length > 0 && (
          <div className="mt-16" data-testid="about-partners-grid">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-[#0A0A0A]/20" />
              <span className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60 flex items-center gap-2">
                <Handshake size={14} className="text-[#FF5A1F]" />
                {t("about.team.partners_label", "Collaborazioni")}
              </span>
              <div className="h-px flex-1 bg-[#0A0A0A]/20" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 border-t-2 border-l-2 border-[#0A0A0A]">
              {partners.map((p) => (
                <a
                  key={p.id}
                  href={p.link || undefined}
                  target={p.link ? "_blank" : undefined}
                  rel={p.link ? "noreferrer" : undefined}
                  className={`aspect-[4/3] border-r-2 border-b-2 border-[#0A0A0A] p-5 flex flex-col justify-between bg-[#FAFAF7] hover:bg-[#0A0A0A] hover:text-[#FAFAF7] transition-colors group ${p.link ? "cursor-pointer" : "cursor-default"}`}
                  data-testid={`partner-card-${p.id}`}
                >
                  {p.image_url ? (
                    <div className="flex-1 flex items-center justify-center">
                      <img src={p.image_url} alt={p.name} className="max-h-full max-w-full object-contain group-hover:invert-0" />
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center">
                      <div className="font-display text-2xl uppercase leading-none">{p.name}</div>
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-widest opacity-60 mt-2">
                    {p[`role_${lang}`] || p.role_it}
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </section>
      )}

      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 border-t-2 border-[#0A0A0A]/10">
        <h2 className="font-display text-5xl md:text-7xl uppercase mb-12 text-[#0A0A0A]">
          {t("about.values.title", "Valori")}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {["01", "02", "03", "04"].map((n) => (
            <div key={n} className="border-2 border-[#0A0A0A] p-6 bg-[#FAFAF7]" data-testid={`value-${n}`}>
              <div className="text-xs font-bold text-[#FF5A1F] tracking-widest">{n}</div>
              <h3 className="font-display text-2xl uppercase mt-3 text-[#0A0A0A]">
                {t(`about.values.${n}.title`, "")}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#0A0A0A]/70">
                {t(`about.values.${n}.text`, "")}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
